import Button from '@material-ui/core/Button';
import SearchIcon from '@material-ui/icons/Search';
import classNames from 'classnames';
import { format } from 'date-fns';
import { ReactElement, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import ListItem from '~/ui/components/common/ListItem';
import Loader from '~/ui/components/common/Loader';
import Input from '~/ui/components/inputs/Input';
import Select from '~/ui/components/inputs/SelectWithoutAnimation';
import EmptyState from '~/ui/pages/Notifications/components/EmptyState';
import FileViewer from '~/ui/pages/Resources/components/FileViewer';

import { useStoreActions, useStoreState } from '~/store/hooks';
import { getLocalDateNoFormat } from '~/utils/date/date';
import formatResource from './helpers/formatResource';
import useDocuments from './hooks/useDocuments';

import { IOption } from '~/types';
import { generalDate } from '~/ui/constants/dateFormat';
import { sortDirectionOptions } from '~/ui/constants/sortingOptions';
import { SortDirection } from '~/ui/pages/Reports/constants/visitsSortOptions';
import orderByOptions from '../../../constants/orderByOptions';
import DocumentsOrderBy from '../../../models/documentsOrderBy';

import { ReactComponent as LoadMoreIcon } from '~/ui/assets/images/loadMore.svg';
import { extractErrorMessage } from '~/utils/error/error';
import formatDictionaryOptions from '~/utils/formatDictionaryOptions';

import { IClinic } from '~/services/api/clinic/types';
import { IDictionaryTypes } from '~/services/api/dictionaries/types';
import { IResource } from '~/services/api/resource/types';

import useClientMultiTeam from '~/hooks/useClientMultiTeamId';
import loaderStyles from '~/ui/components/common/Loader/Loader.module.scss';
import styles from './DocumentsBlock.module.scss';

const DocumentsBlock = (): ReactElement => {
  const [file, setFile] = useState<IResource | null>(null);

  const { current } = useStoreState(state => state.user);
  const client = useStoreState(state => state.client.current);
  const clinic = useStoreState(state => state.clinic.current || ({} as IClinic));
  const documentDictionary =
    useStoreState(
      state => state.dictionaries.availableDictionaries[IDictionaryTypes.DocumentType]?.list,
    ) || [];
  const { getClientMultiTeamId } = useClientMultiTeam();
  const clientTeamId = getClientMultiTeamId(client?.actTeams);

  const onGetDictionariesByType = useStoreActions(
    actions => actions.dictionaries.onGetDictionariesByType,
  );
  const showError = useStoreActions(actions => actions.snackbar.showError);

  const documentDictionaryOptions = formatDictionaryOptions(documentDictionary || []);

  const {
    watch,
    register,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      documentTypeId: null,
      sorting: DocumentsOrderBy.DATE,
      sortDirection: SortDirection.Desc,
    },
  });

  const { name, documentTypeId } = watch();

  const {
    items,
    loading,
    loadMore,
    setSearchQuery,
    setDocumentTypeFilter,
    setDocumentOrderBy,
    setDocumentSortDirection,
  } = useDocuments();

  useEffect(() => {
    setSearchQuery(name);
  }, [name, setSearchQuery]);

  useEffect(() => {
    setDocumentTypeFilter(documentTypeId);
  }, [documentTypeId, setDocumentTypeFilter]);

  useEffect(() => {
    try {
      onGetDictionariesByType({ type: IDictionaryTypes.DocumentType, clinicId: clinic?.id });
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  }, [onGetDictionariesByType, clinic?.id, showError]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.topBlock}>
        <h4 className={styles.title}>Documents</h4>
        <div>
          <Select
            options={orderByOptions}
            name="sorting"
            control={control}
            label="Order By"
            errors={errors}
            hideSelectedOptions={false}
            placeholder="Date"
            onSelect={option => setDocumentOrderBy(option as IOption)}
          />
          <Select
            options={sortDirectionOptions}
            name="sortDirection"
            control={control}
            label="Sort Direction"
            errors={errors}
            hideSelectedOptions={false}
            placeholder="Descending"
            onSelect={option => setDocumentSortDirection(option as IOption)}
          />
          <Select
            options={documentDictionaryOptions}
            name="documentTypeId"
            control={control}
            label="Filter By Type"
            errors={errors}
            hideSelectedOptions={false}
            isClearable
            placeholder="All"
          />
          <Input
            register={register}
            placeholder="Search By Name"
            name="name"
            errors={errors}
            startAdornment={<SearchIcon color="primary" />}
          />
        </div>
      </div>
      {!!loading && <Loader className={loaderStyles.loaderContainer} />}
      {items.length ? (
        items.map((item, idx) => (
          <ListItem
            className={classNames(
              items.length === 1 || items.length - 1 === idx ? styles.noBorder : '',
              styles.itemLabel,
            )}
            key={item.id + item.type}
            label={item.type}
            content={`${item.staff?.length ? `${item.staff} - ` : ''}${format(
              getLocalDateNoFormat(item.date),
              generalDate,
            )}`}
            onPreview={() =>
              setFile(formatResource(item, current.clinic.id, clientTeamId, client.id))
            }
            showPreview
          />
        ))
      ) : (
        <>
          {!items.length && (
            <EmptyState
              className={styles.emptyState}
              noCellWrap
              text="No Documents have been added"
            />
          )}
        </>
      )}
      {loadMore && (
        <div className={styles.paginationButton}>
          <Button
            onClick={loadMore}
            color="primary"
            variant="outlined"
            startIcon={<LoadMoreIcon />}
          >
            Load more
          </Button>
        </div>
      )}
      {file && <FileViewer file={file} onClose={() => setFile(null)} />}
    </div>
  );
};

export default DocumentsBlock;
